/*
 * ssstufff custom behaviour
 * -------------------------
 * Dev-owned JS, version-controlled and reviewed through git. Loaded deferred from
 * layout/theme.liquid. Keep it dependency-free and namespaced — no globals beyond
 * what is documented here.
 */

/*
 * Keep the Kiwi Sizing "Size Chart" button alive across variant changes.
 *
 * Horizon re-renders the variant picker through the Section Rendering API on every
 * variant:update (assets/events.js dispatches it on document, and theme components
 * like assets/local-pickup.js rebind on the same event). That swap replaces the
 * <legend> which the Kiwi app injected its button into on page load. The app only
 * injects once, so the first time a shopper picks a size the Size Chart button
 * disappears and never comes back.
 *
 * Verified on the store rather than assumed: after a size click, .ks-chart-container
 * is gone from the DOM while the app's own <script> block is still present, and
 * calling the app's loader puts it straight back.
 *
 * This listens for the theme's event and asks the app to re-inject, but only when
 * the container has actually gone, so a variant change that leaves it intact costs
 * nothing.
 */
(() => {
  const CONTAINER_SELECTOR = '.ks-chart-container';
  let pending = false;

  const restoreSizeChart = () => {
    pending = false;

    // Still there — the swap left it alone, or a previous pass already restored it.
    if (document.querySelector(CONTAINER_SELECTOR)) return;

    // App absent on this page, or not booted yet. Nothing to re-inject.
    if (typeof window.loadKiwiSizingChart !== 'function') return;

    window.loadKiwiSizingChart();
  };

  document.addEventListener('variant:update', () => {
    if (pending) return;
    pending = true;

    // Two frames so Horizon has finished putting the new markup in place before we
    // decide whether the container is missing.
    requestAnimationFrame(() => requestAnimationFrame(restoreSizeChart));
  });
})();

/*
 * Open the desktop header dropdown when the cursor was already on the menu.
 *
 * Horizon opens submenus from on:pointerenter attributes on each
 * .menu-list__list-item, handled by one document-level listener that
 * assets/component.js registers when the deferred module runs. pointerenter only
 * fires when the cursor crosses into the element, so if the cursor is already
 * resting on a menu item when that listener appears (typical right after clicking
 * a nav link — the next page loads under a parked cursor), the enter is lost.
 * Moving inside the same item fires nothing; only crossing into a sibling does,
 * which is why the menu "unsticks" when you roll over another item.
 *
 * Reproduced deterministically on the live store: load with the cursor on
 * "Products", wiggle inside it (stays closed), cross to "Capsules" (opens).
 *
 * This watches pointermove, and when the cursor is over a closed item it re-fires
 * the theme's own activation event — the same synthetic-pointerenter trick
 * Horizon's #reconcilePointerTarget uses in assets/header-menu.js. The
 * aria-expanded guard means it fires at most once per missed activation (activate
 * flips it to "true" synchronously) and skips items with no submenu, which never
 * get the attribute.
 */
(() => {
  document.addEventListener(
    'pointermove',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const item = target.closest('header-menu .menu-list__list-item');
      if (!item) return;

      const link = item.querySelector('[ref="menuitem"]');
      if (!link || link.getAttribute('aria-expanded') !== 'false') return;

      item.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
    },
    { passive: true }
  );
})();

/*
 * Card image blur-up placeholders.
 *
 * snippets/product-media.liquid renders a 20px copy of each card slide image as
 * <img class="product-media__placeholder" data-lqip data-src="..."> under the real image
 * (styled in assets/ssstufff-custom.css). Two jobs here:
 *
 * 1. Fetch placeholders per card, not per page. A card's placeholders get their src when
 *    the card comes within PLACEHOLDER_MARGIN of the viewport, so a 24-card collection costs
 *    nothing for the cards a shopper never scrolls to. The first four cards of a section
 *    arrive with src already set (blocks/_product-card.liquid) and need nothing from here.
 *
 * 2. Fade the placeholder out once the real image has loaded, by marking the wrapper
 *    .product-media--loaded. Needed because some product images are transparent PNGs and
 *    the blur would show through them. The load event does not bubble, so this listens in
 *    the capture phase on the document, which also covers cards that arrive later through
 *    infinite scroll, filters, recommendations and quick add.
 */
(() => {
  // Two wrapper flavours share the mechanism: product cards (.product-media, placeholder
  // rendered by snippets/product-media.liquid) and everything else (.lqip, placeholder
  // rendered by snippets/ssstufff-lqip.liquid: the hero and the bento tiles).
  const PLACEHOLDER = 'img[data-lqip][data-src]';
  const REAL = 'img.product-media__image, img.lqip__image';
  const WRAPPER = '.product-media, .lqip';
  const CARD = '.card-gallery';
  const PLACEHOLDER_MARGIN = '300px';
  const FADE_CLASS = 'lqip-fade';

  const markLoaded = (img) => {
    // Dropping the fade class returns the image to whatever opacity the theme's own CSS
    // gives it (1, or the sold-out treatment), with the transition declared in custom.css.
    img.classList.remove(FADE_CLASS);
    const wrapper = img.parentElement;
    if (!wrapper) return;
    if (wrapper.classList.contains('product-media')) wrapper.classList.add('product-media--loaded');
    if (wrapper.classList.contains('lqip')) wrapper.classList.add('lqip--loaded');
  };

  document.addEventListener(
    'load',
    (event) => {
      const img = event.target;
      if (img instanceof HTMLImageElement && img.matches(REAL)) {
        markLoaded(img);
      }
    },
    true
  );

  const activateCard = (card) => {
    for (const img of card.querySelectorAll(PLACEHOLDER)) {
      const src = img.getAttribute('data-src');
      img.removeAttribute('data-src');
      if (src) img.setAttribute('src', src);
    }
  };

  // 3. Warm the slide after the current one when a shopper shows intent: a finger or
  //    pointer landing on a card gallery, and every slide change after that. Dropping
  //    loading="lazy" makes the browser fetch it now, so it is usually there before the
  //    swipe ends. One image per touched card, nothing for cards nobody touches.
  const warmNext = (gallery) => {
    const slideshow = gallery.querySelector('slideshow-component');
    if (!slideshow) return;
    const slides = slideshow.querySelectorAll('slideshow-slide');
    const current = typeof slideshow.current === 'number' ? slideshow.current : 0;
    const next = slides[current + 1];
    next?.querySelector('img.product-media__image[loading="lazy"]')?.removeAttribute('loading');
    activateCard(gallery);
  };

  document.addEventListener(
    'pointerdown',
    (event) => {
      const gallery = event.target instanceof Element ? event.target.closest(CARD) : null;
      if (gallery) warmNext(gallery);
    },
    { capture: true, passive: true }
  );

  document.addEventListener('slideshow:select', (event) => {
    const gallery = event.target instanceof Element ? event.target.closest(CARD) : null;
    if (gallery) warmNext(gallery);
  });

  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              observer.unobserve(entry.target);
              activateCard(entry.target);
            }
          },
          { rootMargin: PLACEHOLDER_MARGIN }
        )
      : null;

  const watched = new WeakSet();

  const scan = (root) => {
    // Real images that finished before this script ran, or that came in with the page cached.
    // A lazy card image still pending gets the fade class so it eases in over the blur
    // instead of popping. Only lazy ones: the eager first images are the page's LCP and
    // must never wait on this script.
    for (const img of root.querySelectorAll(REAL)) {
      if (img.complete && img.naturalWidth > 0) {
        markLoaded(img);
      } else if (img.getAttribute('loading') === 'lazy' && img.parentElement?.querySelector(':scope > [data-lqip]')) {
        img.classList.add(FADE_CLASS);
      }
    }

    for (const placeholder of root.querySelectorAll(PLACEHOLDER)) {
      // Watch the whole card gallery where there is one (all five slides light up together),
      // otherwise the placeholder's own wrapper (a bento tile).
      const target = placeholder.closest(CARD) || placeholder.closest(WRAPPER);
      if (!target) {
        activateCard(placeholder.parentElement);
        continue;
      }
      if (watched.has(target)) continue;
      watched.add(target);
      if (observer) {
        observer.observe(target);
      } else {
        activateCard(target);
      }
    }
  };

  scan(document);

  // Cards added after load (infinite scroll, filter results, recommendations, quick add).
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      scan(document);
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
